import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils/cn';

type CardAccent = 'primary' | 'success' | 'warning' | 'danger' | 'shield' | 'detect' | 'trust' | 'signal' | 'none';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  noPadding?: boolean;
  accent?: CardAccent;
  elevated?: boolean;
  glass?: boolean;
}

const accentClass: Record<CardAccent, string> = {
  none:    '',
  primary: 'card-accent-top card-accent-primary',
  success: 'card-accent-top card-accent-success',
  warning: 'card-accent-top card-accent-warning',
  danger:  'card-accent-top card-accent-danger',
  shield:  'card-accent-top card-accent-shield',
  detect:  'card-accent-top card-accent-detect',
  trust:   'card-accent-top card-accent-trust',
  signal:  'card-accent-top card-accent-signal',
};

export function Card({ children, className, noPadding, accent = 'none', elevated, glass, ...rest }: CardProps) {
  return (
    <div
      className={cn(
        'rounded-2xl border border-border',
        glass
          ? 'bg-surface/80 backdrop-blur-sm'
          : 'bg-surface',
        elevated ? 'shadow-card-md dark:shadow-card-dark' : 'shadow-card dark:shadow-card-dark',
        !noPadding && 'p-4 sm:p-5',
        accentClass[accent],
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function CardHeader({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('mb-4 flex items-center justify-between gap-2', className)}>
      {children}
    </div>
  );
}

export function CardTitle({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <h3 className={cn('text-[13.5px] font-semibold tracking-tight text-fg', className)}>
      {children}
    </h3>
  );
}

export function CardDescription({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cn('text-sm text-fg-muted', className)}>{children}</p>;
}
